import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { WhatsappIntegrationData } from '@/types/onboarding';
import { useOnboardingSteps } from '@/hooks/use-onboarding-steps';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Loader2, 
  ArrowLeft, 
  CheckCircle, 
  PhoneIcon, 
  QrCode, 
  ArrowRight, 
  PencilIcon 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { StepLayout } from '../StepLayout';
import { onboardingService } from "@/services/onboardingService";

interface WhatsAppStepProps {
  onNext: (data: { whatsapp: WhatsappIntegrationData, __internal_save_only?: boolean }) => void;
  data?: Partial<WhatsappIntegrationData>;
}

// Componente de passo do stepper interno
const StepperItem = ({ 
  step, 
  isActive, 
  isCompleted, 
  label, 
  description 
}: { 
  step: number; 
  isActive: boolean; 
  isCompleted: boolean; 
  label: string; 
  description: string;
}) => {
  return (
    <div className="flex flex-col items-center">
      <div 
        className={cn(
          "flex items-center justify-center w-10 h-10 rounded-full text-sm font-medium transition-colors", 
          isActive && !isCompleted ? "bg-primary text-primary-foreground" : "",
          isCompleted ? "bg-green-100 text-green-700 border-2 border-green-500" : "",
          !isActive && !isCompleted ? "bg-muted text-muted-foreground" : ""
        )}
      >
        {isCompleted ? <CheckCircle className="h-5 w-5" /> : step}
      </div>
      <p className={cn(
        "mt-2 text-sm font-medium", 
        isActive ? "text-primary" : "text-muted-foreground"
      )}>
        {label}
      </p>
      <p className="text-xs text-muted-foreground max-w-[130px] text-center">
        {description}
      </p>
    </div>
  );
};

export const WhatsAppStep: React.FC<WhatsAppStepProps> = ({ onNext, data = {} }) => {
  const { toast } = useToast();
  const { steps, getStepById } = useOnboardingSteps();
  const [phone, setPhone] = useState(data.phone_number || '55');
  const [isCreatingInstance, setIsCreatingInstance] = useState(false);
  const [isGettingQrCode, setIsGettingQrCode] = useState(false);
  const [isConnected, setIsConnected] = useState(!!data.is_connected);
  const [error, setError] = useState<string | null>(null);
  const [instanceCreated, setInstanceCreated] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(data.qr_code || null);
  const [currentStep, setCurrentStep] = useState<'input' | 'qr'>('input');
  const [instanceName, setInstanceName] = useState<string | null>(data.instance_name || null);
  const [isCheckingInstance, setIsCheckingInstance] = useState(false);

  // Se já temos dados do WhatsApp, mostrar o QR Code
  useEffect(() => {
    if (data.phone_number) {
      setPhone(data.phone_number);
      
      if (data.instance_name) {
        setInstanceName(data.instance_name);
        setInstanceCreated(true);
        
        // Se temos o nome da instância, devemos mostrar a etapa do QR code
        setCurrentStep('qr');
        
        // Se além da instância também temos o QR code
        if (data.qr_code) {
          setQrCode(data.qr_code);
          
          // Se ainda não está conectado, iniciar polling de verificação
          if (!data.is_connected) {
            startPolling();
          }
        } else {
          // Se temos a instância mas não temos o QR code, gerar automaticamente
          handleGetQrCode();
        }
      } else {
        // Se temos apenas o número de telefone, verificar se existe uma instância
        checkForExistingInstance(data.phone_number);
      }
    }
  }, [data]);

  // Verificar se o usuário já tem uma instância ao inicializar o componente
  useEffect(() => {
    // Se já estamos com um QR code ou se a instância está conectada, não precisamos verificar
    if (qrCode || isConnected || !data.phone_number) return;
    
    // Verificar se a instância já existe para o número armazenado
    checkForExistingInstance(data.phone_number);
  }, []);

  // Nova função para verificar se o usuário já tem uma instância criada
  const checkForExistingInstance = async (phoneNumber: string) => {
    if (!phoneNumber || phoneNumber.length < 10) return false;
    
    try {
      setIsCheckingInstance(true);
      setError(null);
      
      // Remove qualquer caractere que não seja número
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const instanceNameToCheck = `user_${cleanPhone}`;
      
      console.log(`Verificando se existe instância para o número ${cleanPhone}...`);
      
      // Verifica se a instância já existe
      const { data: response, error } = await supabase.functions.invoke('evolution-api-proxy', {
        body: {
          action: 'status',
          instanceName: instanceNameToCheck
        }
      });
      
      if (error) {
        console.error('Erro ao verificar instância:', error);
        return false;
      }
      
      console.log('Resposta da verificação de instância:', response);
      
      // Se a resposta tem um status ou mesmo um erro controlado, a instância provavelmente existe
      if (response) {
        console.log('Status da instância existente:', response);
        
        // Se a instância existe e está conectada
        if (response.status === 'open') {
          setIsConnected(true);
          setInstanceCreated(true);
          setInstanceName(instanceNameToCheck);
          
          // Salvar o progresso com a instância já conectada
          saveProgress({
            phone_number: cleanPhone,
            instance_name: instanceNameToCheck,
            is_connected: true
          });
          
          toast({
            title: "WhatsApp já conectado",
            description: "Seu WhatsApp já está conectado. Avançando para a próxima etapa."
          });
          
          // Avança diretamente para a próxima etapa
          onNext({
            whatsapp: {
              phone_number: cleanPhone,
              instance_name: instanceNameToCheck,
              is_connected: true
            }
          });
          
          return true;
        } 
        // Se a instância existe mas não está conectada (status connecting, created ou outro)
        else if (response.status) {
          console.log(`Instância encontrada com status ${response.status}`);
          setInstanceCreated(true);
          setInstanceName(instanceNameToCheck);
          setCurrentStep('qr');
          
          toast({
            title: "Instância encontrada",
            description: "Encontramos uma instância de WhatsApp para seu número. Gerando QR code para conexão."
          });
          
          // Gera o QR code automaticamente
          handleGetQrCode();
          
          return true;
        }
        // Detectar existência da instância mesmo quando retorna um erro conhecido
        else if (response.message && response.message.includes('instance exists')) {
          console.log('Instância detectada através de mensagem de erro');
          setInstanceCreated(true);
          setInstanceName(instanceNameToCheck);
          setCurrentStep('qr');
          
          // Gera o QR code automaticamente
          handleGetQrCode();
          
          return true;
        }
      }
      
      return false;
    } catch (err) {
      console.error('Erro ao verificar instância existente:', err);
      return false;
    } finally {
      setIsCheckingInstance(false);
    }
  };

  // Função para salvar o progresso sem avançar para a próxima etapa
  const saveProgress = (whatsappData: Partial<WhatsappIntegrationData>) => {
    // Merge com os dados existentes
    const updatedData: WhatsappIntegrationData = {
      ...((data || {}) as WhatsappIntegrationData),
      ...whatsappData
    };
    
    // Usar a mesma função onNext, mas com um flag internal_save para indicar
    // que é apenas para salvar, não para avançar
    onNext({ 
      whatsapp: updatedData, 
      __internal_save_only: true // Este flag deve ser verificado no componente pai
    });
  };

  // Modificar a função handlePhoneChange para manter o prefixo 55 e verificar instâncias existentes
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove qualquer caractere que não seja número
    let value = e.target.value.replace(/\D/g, '');
    
    // Garantir que sempre comece com 55 (Brasil)
    if (!value.startsWith('55')) {
      value = '55' + value.substring(value.startsWith('55') ? 2 : 0);
    }
    
    setPhone(value);
  };

  const handleBack = () => {
    setCurrentStep('input');
    // Não limpar os dados, apenas voltar para a primeira etapa
  };

  const handleNext = () => {
    if (instanceCreated && instanceName) {
      setCurrentStep('qr');
    }
  };

  const handleCreateInstance = async () => {
    // Validar o formato do número de telefone
    if (!phone || phone.length < 10) {
      toast({
        title: "Número inválido",
        description: "Digite um número de telefone válido com pelo menos 10 dígitos",
        variant: "destructive"
      });
      return;
    }
    
    // Verificar se já existe uma instância
    const hasExistingInstance = await checkForExistingInstance(phone);
    if (hasExistingInstance) return;
    
    try {
      setIsCreatingInstance(true);
      setError(null);

      const { data: response, error } = await supabase.functions.invoke('evolution-api-proxy', {
        body: {
          action: 'create',
          instanceName: `user_${phone.replace(/\D/g, '')}`,
          number: phone
        }
      });

      if (error) throw error;

      // Guarde o nome da instância
      const newInstanceName = `user_${phone.replace(/\D/g, '')}`;
      setInstanceCreated(true);
      setInstanceName(newInstanceName);
      setCurrentStep('qr');
      
      // Salvar o progresso atual sem avançar para a próxima etapa
      const progressData: Partial<WhatsappIntegrationData> = {
        phone_number: phone,
        instance_name: newInstanceName
      };
      
      // Se a URL do webhook foi configurada, salvar também
      if (response?.webhook_url) {
        progressData.webhook_url = response.webhook_url;
      }
      
      // Se informações da credencial estiverem disponíveis, salvá-las também
      if (response?.credential_created) {
        progressData.credential_created = response.credential_created;
        progressData.credential_id = response.credential_id;
        progressData.credential_name = response.credential_name;
        
        // Salvar credenciais no integration_config para uso futuro
        try {
          const userId = (await supabase.auth.getUser()).data.user?.id;
          if (userId) {
            await onboardingService.saveEvolutionApiCredentials(userId, {
              credential_id: response.credential_id,
              credential_name: response.credential_name,
              instance_name: newInstanceName
            });
          }
        } catch (credentialError) {
          console.error("Erro ao salvar credenciais no integration_config:", credentialError);
          // Não interrompe o fluxo se falhar
        }
      }
      
      saveProgress(progressData);
      
      let toastDescription = 'Agora você pode gerar o QR Code para conectar seu WhatsApp.';
      let configSuccess = true;
      
      // Verificar se as configurações foram aplicadas
      if (response) {
        if (response.settings_configured) {
          toastDescription += ' Configurações aplicadas com sucesso.';
        } else if (typeof response.settings_configured !== 'undefined') {
          console.warn('Falha ao aplicar configurações:', response.settings_error);
          toastDescription += ' Configurações padrão foram aplicadas.';
          configSuccess = false;
        }
        
        // Verificar se o webhook foi configurado
        if (response.webhook_configured) {
          toastDescription += ' Webhook configurado com sucesso.';
        } else if (typeof response.webhook_configured !== 'undefined') {
          console.warn('Falha ao configurar webhook:', response.webhook_error);
          toastDescription += ' Webhook padrão foi aplicado.';
          configSuccess = false;
        }
        
        // Verificar se a credencial foi criada
        if (response.credential_created) {
          toastDescription += ' Credencial n8n criada com sucesso.';
        } else if (typeof response.credential_created !== 'undefined') {
          console.warn('Falha ao criar credencial n8n:', response.credential_error);
          toastDescription += ' Não foi possível criar credencial n8n.';
          configSuccess = false;
        }
      }
      
      toast({
        title: 'Instância criada com sucesso!',
        description: toastDescription,
        variant: configSuccess ? 'default' : 'destructive'
      });
    } catch (error) {
      console.error('Erro ao criar instância:', error);
      setError('Erro ao criar instância. Por favor, tente novamente.');
    } finally {
      setIsCreatingInstance(false);
    }
  };

  const handleGetQrCode = async () => {
    if (!instanceName) {
      setError('Instância não criada. Volte e crie a instância primeiro.');
      return;
    }
    
    try {
      setIsGettingQrCode(true);
      setError(null);

      const { data, error } = await supabase.functions.invoke('evolution-api-proxy', {
        body: {
          action: 'connect',
          instanceName: instanceName
        }
      });

      if (error) throw error;

      if (data?.base64) {
        setQrCode(data.base64);
        
        // Salvar o QR Code no estado de onboarding sem avançar para a próxima etapa
        saveProgress({
          phone_number: phone,
          instance_name: instanceName,
          qr_code: data.base64
        });
        
        // Iniciar polling para verificar o status da conexão
        startPolling();
      } else {
        throw new Error('QR Code não encontrado na resposta');
      }
    } catch (error) {
      console.error('Erro ao obter QR Code:', error);
      setError('Erro ao obter QR Code. Por favor, tente novamente.');
    } finally {
      setIsGettingQrCode(false);
    }
  };

  const startPolling = () => {
    // Limpar qualquer intervalo existente
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    
    // Criar um novo intervalo para verificar o status da conexão a cada 5 segundos
    const interval = setInterval(async () => {
      try {
        if (!instanceName) {
          console.error('Nome da instância não encontrado para polling');
          clearInterval(interval);
          setPollingInterval(null);
          return;
        }
        
        console.log('Verificando status da conexão para:', instanceName);
        
        const { data, error } = await supabase.functions.invoke('evolution-api-proxy', {
          body: {
            action: 'status',
            instanceName
          }
        });
        
        if (error) {
          console.error('Erro ao verificar status durante polling:', error);
          return;
        }
        
        console.log('Status da conexão:', data);
        
        // Se a instância estiver conectada, avançar para o próximo passo
        if (data?.status === 'open') {
          setIsConnected(true);
          
          // Limpar intervalo
          clearInterval(interval);
          setPollingInterval(null);
          
          // Salvar dados da instância e avançar para próxima etapa
          const whatsappData: WhatsappIntegrationData = {
            phone_number: phone,
            instance_name: instanceName,
            is_connected: true,
            qr_code_scanned: true,
            qr_code: qrCode, // Manter o QR code para referência
            webhook_url: data?.webhook_url,
            credential_id: data?.credential_id,
            credential_name: data?.credential_name,
            credential_created: data?.credential_created
          };
          
          toast({
            title: 'WhatsApp conectado com sucesso!',
            description: 'Você será direcionado para o próximo passo.',
          });
          
          // Avançar para próxima etapa
          onNext({ whatsapp: whatsappData });
        }
      } catch (err) {
        console.error('Erro durante polling de status:', err);
      }
    }, 5000);
    
    setPollingInterval(interval);
  };

  // Limpar intervalo quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Título e ícone para o StepLayout, com base na etapa atual
  const stepTitle = currentStep === 'input' 
    ? 'Configurar Instância WhatsApp' 
    : 'Conectar WhatsApp';
  
  const stepIcon = currentStep === 'input' 
    ? <PhoneIcon className="h-5 w-5" /> 
    : <QrCode className="h-5 w-5" />;

  return (
    <StepLayout 
      title={stepTitle}
      icon={stepIcon}
    >
      {/* Sub-stepper para as etapas do WhatsApp */}
      <div className="mb-8">
        <div className="flex justify-center items-start">
          <div className="flex items-center">
            <StepperItem 
              step={1} 
              isActive={currentStep === 'input'} 
              isCompleted={instanceCreated && currentStep === 'qr'} 
              label="Configurar Instância" 
              description="Crie sua instância WhatsApp"
            />
            <div className="mx-4 w-20 h-0.5 bg-muted relative">
              <div 
                className="absolute top-0 left-0 h-full bg-primary transition-all"
                style={{ width: instanceCreated ? '100%' : '0%' }}
              />
            </div>
            <StepperItem 
              step={2} 
              isActive={currentStep === 'qr'} 
              isCompleted={isConnected} 
              label="Conectar WhatsApp" 
              description="Escaneie o QR Code"
            />
          </div>
        </div>
      </div>
      
      {/* Conteúdo das etapas */}
      {currentStep === 'input' ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 border p-4 rounded-lg bg-muted/30">
            <PhoneIcon className="h-5 w-5 text-muted-foreground" />
            <div className="flex-1 relative">
              <Input
                id="phone"
                type="tel"
                placeholder="5599999999999"
                value={phone}
                onChange={handlePhoneChange}
                disabled={isCreatingInstance || isCheckingInstance}
                className="bg-background pl-8"
              />
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground text-sm">
                +
              </span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            O código do país (Brasil +55) já está incluído. Digite apenas o DDD e o número.
          </p>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              onClick={handleCreateInstance}
              disabled={!phone || isCreatingInstance || phone.length < 10}
              variant="default"
              className="w-full sm:w-auto"
            >
              {isCreatingInstance ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Criando instância...
                </>
              ) : (
                <>
                  Criar Instância
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
          
          {instanceCreated && (
            <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <p className="text-sm text-green-700">
                Instância já criada! Você pode 
                <Button variant="link" className="px-1 h-auto" onClick={handleNext}>
                  avançar para o próximo passo
                </Button>
                ou criar uma nova.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleBack}
              className="gap-1"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium">
                Número: {phone}
              </p>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBack} 
                className="h-8 w-8 p-0"
              >
                <PencilIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {instanceCreated && !qrCode && (
            <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
              <p className="text-sm text-green-700">
                Instância criada com sucesso! Agora vamos gerar o QR Code.
              </p>
            </div>
          )}

          {data?.credential_created && (
            <div className="flex items-center p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-blue-500 mr-2 flex-shrink-0" />
              <p className="text-sm text-blue-700">
                Credencial n8n criada com sucesso! ID: {data.credential_id?.substring(0, 8)}...
              </p>
            </div>
          )}

          {!qrCode ? (
            <div className="flex flex-col items-center justify-center p-8 border border-dashed rounded-lg bg-muted/30">
              <QrCode className="h-16 w-16 text-muted-foreground mb-4" />
              <p className="text-center mb-4 text-muted-foreground">
                Clique no botão abaixo para gerar o QR Code
              </p>
              <Button
                onClick={handleGetQrCode}
                disabled={isGettingQrCode}
              >
                {isGettingQrCode ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Gerando QR Code...
                  </>
                ) : (
                  'Gerar QR Code'
                )}
              </Button>
            </div>
          ) : (
            <div className="border rounded-lg p-6 bg-white">
              <div className="flex flex-col items-center">
                <div className="text-center mb-4">
                  <h3 className="text-base font-medium">Escaneie o QR Code</h3>
                  <p className="text-sm text-muted-foreground">
                    Abra o WhatsApp no seu celular e escaneie o código
                  </p>
                </div>
                
                <div className="bg-white p-3 border rounded-lg shadow-sm">
                  <img
                    src={qrCode}
                    alt="QR Code WhatsApp"
                    className="mx-auto"
                    style={{ maxWidth: "220px", maxHeight: "220px" }}
                  />
                </div>
                
                <div className="mt-6 w-full">
                  <div className="flex items-center justify-center gap-2 py-2 px-3 rounded-md bg-muted/30 border">
                    <Loader2 className="h-4 w-4 text-zapcore animate-spin" />
                    <span className="text-sm">Verificando conexão automaticamente...</span>
                  </div>
                  <div className="flex items-center justify-center mt-3 gap-1 text-xs text-muted-foreground">
                    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-muted-foreground"><path d="M7.49991 0.877045C3.84222 0.877045 0.877045 3.84222 0.877045 7.49991C0.877045 11.1576 3.84222 14.1228 7.49991 14.1228C11.1576 14.1228 14.1228 11.1576 14.1228 7.49991C14.1228 3.84222 11.1576 0.877045 7.49991 0.877045ZM1.82704 7.49991C1.82704 4.36689 4.36689 1.82704 7.49991 1.82704C10.6329 1.82704 13.1728 4.36689 13.1728 7.49991C13.1728 10.6329 10.6329 13.1728 7.49991 13.1728C4.36689 13.1728 1.82704 10.6329 1.82704 7.49991ZM8.24992 4.49999C8.24992 4.9142 7.91413 5.24999 7.49992 5.24999C7.08571 5.24999 6.74992 4.9142 6.74992 4.49999C6.74992 4.08577 7.08571 3.74999 7.49992 3.74999C7.91413 3.74999 8.24992 4.08577 8.24992 4.49999ZM6.00003 5.99999H6.50003H7.50003C7.77618 5.99999 8.00003 6.22384 8.00003 6.49999V9.99999H8.50003H9.00003V11H8.50003H7.50003H6.50003H6.00003V9.99999H6.50003H7.00003V6.99999H6.50003H6.00003V5.99999Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
                    <span>
                      Conecte seu WhatsApp escaneando o QR code acima
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </StepLayout>
  );
}; 